// Gedeelde evaluatie van `Product.specifications.dimensionConstraint`.
//
// De configurator en de server (prijsberekening en order-create) moeten
// exact dezelfde regel toepassen; anders laat de API een maat door die de
// klant in de browser nooit had kunnen kiezen. Dit bestand heeft bewust geen
// imports: het draait zowel in een client component als op de server.

export const DEFAULT_DIMENSION_MESSAGE = 'Deze maat valt buiten het beschikbare formaat.';

export type DimensionRule = {
  conditions?: Record<string, unknown>;
  minWidthCm?: number;
  maxWidthCm?: number;
  minHeightCm?: number;
  maxHeightCm?: number;
  reversible?: boolean;
  minAreaM2?: number;
  message?: string;
};

export type DimensionConstraint = {
  widthOptionId?: string;
  heightOptionId?: string;
  widthByOption?: Record<string, number>;
  rules?: DimensionRule[];
  minSideCm?: number;
  maxShortSideCm?: number;
  maxLongSideCm?: number;
  message?: string;
};

export type DimensionValidation = {
  valid: boolean;
  message: string;
  width?: number;
  height?: number;
};

type Configuration = Record<string, unknown>;

/** Eerste regel waarvan alle `conditions` overeenkomen met de configuratie. */
export function findMatchingDimensionRule(
  constraint: DimensionConstraint | null | undefined,
  config: Configuration,
): DimensionRule | null {
  if (!constraint || !Array.isArray(constraint.rules)) return null;
  return constraint.rules.find((rule) => {
    const conditions = rule?.conditions || {};
    return Object.entries(conditions).every(([key, expected]) => {
      const selected = config[key];
      return Array.isArray(expected)
        ? expected.map(String).includes(String(selected))
        : String(selected) === String(expected);
    });
  }) || null;
}

/**
 * Grenzen voor het invoerveld van één as. Bij een omkeerbare regel mag elke
 * zijde de lange zijde zijn (400 × 196 én 196 × 400), dus krijgt elk veld de
 * ruimste grenzen van beide assen; de echte controle blijft
 * `evaluateDimensionConstraint`.
 */
export function getDimensionRuleInputBounds(
  rule: DimensionRule,
  axis: 'width' | 'height',
): { min?: number; max?: number } {
  const own = axis === 'width'
    ? { min: rule.minWidthCm, max: rule.maxWidthCm }
    : { min: rule.minHeightCm, max: rule.maxHeightCm };
  if (rule.reversible !== true) return own;
  const other = axis === 'width'
    ? { min: rule.minHeightCm, max: rule.maxHeightCm }
    : { min: rule.minWidthCm, max: rule.maxWidthCm };
  const pick = (a: number | undefined, b: number | undefined, choose: (x: number, y: number) => number) =>
    a == null ? b : b == null ? a : choose(a, b);
  return { min: pick(own.min, other.min, Math.min), max: pick(own.max, other.max, Math.max) };
}

export function evaluateDimensionConstraint(
  constraint: DimensionConstraint | null | undefined,
  config: Configuration,
): DimensionValidation {
  if (!constraint) return { valid: true, message: '' };

  const widthOptionId = constraint.widthOptionId || 'width_cm';
  const heightOptionId = constraint.heightOptionId || 'height_cm';
  let width = Number(config[widthOptionId]);
  const height = Number(config[heightOptionId]);
  if ((!Number.isFinite(width) || width <= 0) && constraint.widthByOption) {
    width = Number(constraint.widthByOption[String(config[widthOptionId])]);
  }
  // Zonder bruikbare maat valt er niets te controleren; de prijsberekening
  // weigert ontbrekende maten zelf waar dat nodig is.
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { valid: true, message: '' };
  }

  const matchingRule = findMatchingDimensionRule(constraint, config);
  if (matchingRule) {
    const minHeight = Number(matchingRule.minHeightCm || 0);
    const maxHeight = Number(matchingRule.maxHeightCm || 0);
    const minWidth = Number(matchingRule.minWidthCm || 0);
    const maxWidth = Number(matchingRule.maxWidthCm || 0);
    const minAreaM2 = Number(matchingRule.minAreaM2 || 0);
    const areaM2 = (width * height) / 10000;
    const fits = (candidateWidth: number, candidateHeight: number) =>
      (!minHeight || candidateHeight >= minHeight) &&
      (!maxHeight || candidateHeight <= maxHeight) &&
      (!minWidth || candidateWidth >= minWidth) &&
      (!maxWidth || candidateWidth <= maxWidth);
    const valid =
      (fits(width, height) || (matchingRule.reversible === true && fits(height, width))) &&
      (!minAreaM2 || areaM2 >= minAreaM2);
    return {
      valid,
      message: matchingRule.message || constraint.message || DEFAULT_DIMENSION_MESSAGE,
      width,
      height,
    };
  }

  const minSide = Number(constraint.minSideCm || 0);
  const maxShortSide = Number(constraint.maxShortSideCm || 0);
  const maxLongSide = Number(constraint.maxLongSideCm || 0);
  const shortSide = Math.min(width, height);
  const longSide = Math.max(width, height);
  const valid =
    (!minSide || shortSide >= minSide) &&
    (!maxShortSide || shortSide <= maxShortSide) &&
    (!maxLongSide || longSide <= maxLongSide);

  return {
    valid,
    message: constraint.message || DEFAULT_DIMENSION_MESSAGE,
    width,
    height,
  };
}

export class DimensionConstraintError extends Error {
  readonly code = 'DIMENSION_OUT_OF_RANGE';

  constructor(message: string) {
    super(message);
    this.name = 'DimensionConstraintError';
  }
}

export function isDimensionConstraintError(error: unknown): error is DimensionConstraintError {
  return error instanceof DimensionConstraintError
    || (Boolean(error) && typeof error === 'object' && (error as { code?: unknown }).code === 'DIMENSION_OUT_OF_RANGE');
}

/** Gooit een `DimensionConstraintError` wanneer de maat buiten de productregel valt. */
export function assertDimensionConstraint(
  specifications: Record<string, any> | null | undefined,
  config: Configuration,
) {
  const result = evaluateDimensionConstraint(specifications?.dimensionConstraint, config);
  if (!result.valid) {
    const prefix = `Maat ${result.width} × ${result.height} cm valt buiten het beschikbare formaat.`;
    throw new DimensionConstraintError(
      result.message === DEFAULT_DIMENSION_MESSAGE ? prefix : `${prefix} ${result.message}`,
    );
  }
}
